import {
  NETCODE_V4_CLOCK_HALF_RTT_ALPHA,
  NETCODE_V4_CLOCK_HOLD_NEWEST_MS,
  NETCODE_V4_CLOCK_INSTABILITY_CLAMP_MS,
  NETCODE_V4_CLOCK_INSTABILITY_JUMP_MS,
  NETCODE_V4_CLOCK_OFFSET_ALPHA,
  NETCODE_V4_CLOCK_UNSTABLE_SAMPLE_LIMIT,
  NETCODE_V4_FIXED_DT_SECONDS,
  NETCODE_V4_INPUT_FUTURE_WINDOW_TICKS,
  NETCODE_V4_RENDER_DELAY_TICKS,
  NETCODE_V4_TICK_RATE,
} from '@star-snake/shared'

export class ClockSyncV4 {
  private halfRttMs = 0
  private hasHalfRtt = false
  private offsetMs = 0
  private hasOffset = false
  private lastServerMonoMs = 0
  private lastServerTick = 0
  private clockUnstableCount = 0
  private pendingResync = false
  private holdNewestUntilMonoMs = 0

  onServerSample(serverTick: number, serverMonoMs: number, nowMonoMs = performance.now()): void {
    const sampleOffset = serverMonoMs + (this.hasHalfRtt ? this.halfRttMs : 0) - nowMonoMs
    this.applyOffsetSample(sampleOffset, nowMonoMs)
    this.lastServerTick = serverTick >>> 0
    this.lastServerMonoMs = serverMonoMs
  }

  onPong(clientMonoMs: number, serverMonoMs: number, nowMonoMs = performance.now()): void {
    const sampleRttMs = Math.max(0, nowMonoMs - clientMonoMs)
    const sampleHalfRttMs = sampleRttMs * 0.5
    this.halfRttMs = this.hasHalfRtt
      ? this.halfRttMs * (1 - NETCODE_V4_CLOCK_HALF_RTT_ALPHA) + sampleHalfRttMs * NETCODE_V4_CLOCK_HALF_RTT_ALPHA
      : sampleHalfRttMs
    this.hasHalfRtt = true

    const sampleOffset = serverMonoMs + this.halfRttMs - nowMonoMs
    this.applyOffsetSample(sampleOffset, nowMonoMs)
    if (serverMonoMs > this.lastServerMonoMs) {
      this.lastServerMonoMs = serverMonoMs
    }
  }

  estimateServerMonoMs(nowMonoMs = performance.now()): number {
    if (!this.hasOffset) return this.lastServerMonoMs
    return nowMonoMs + this.offsetMs
  }

  estimateServerTick(nowMonoMs = performance.now()): number {
    if (!this.hasOffset) return this.lastServerTick
    const deltaMs = this.estimateServerMonoMs(nowMonoMs) - this.lastServerMonoMs
    return this.lastServerTick + deltaMs / (NETCODE_V4_FIXED_DT_SECONDS * 1000)
  }

  getRenderTick(nowMonoMs = performance.now()): number {
    return this.estimateServerTick(nowMonoMs) - NETCODE_V4_RENDER_DELAY_TICKS
  }

  getNextInputTick(nowMonoMs = performance.now(), fallbackTick = 0): number {
    const fallback = fallbackTick >>> 0
    const estimatedTick = Math.floor(this.estimateServerTick(nowMonoMs) + 1)
    if (!Number.isFinite(estimatedTick)) return fallback

    const minTick = fallback
    const maxTick = Math.max(minTick, (this.lastServerTick >>> 0) + NETCODE_V4_INPUT_FUTURE_WINDOW_TICKS)
    if (estimatedTick <= minTick) return minTick
    if (estimatedTick >= maxTick) return maxTick
    return estimatedTick >>> 0
  }

  noteServerTick(serverTick: number): void {
    this.lastServerTick = Math.max(this.lastServerTick, serverTick >>> 0)
  }

  consumeResyncRequest(): boolean {
    if (!this.pendingResync) return false
    this.pendingResync = false
    return true
  }

  shouldHoldNewest(nowMonoMs = performance.now()): boolean {
    return nowMonoMs <= this.holdNewestUntilMonoMs
  }

  getRttMsFromPong(clientMonoMs: number, nowMonoMs = performance.now()): number {
    return Math.max(0, nowMonoMs - clientMonoMs)
  }

  getTickDurationMs(): number {
    return 1000 / NETCODE_V4_TICK_RATE
  }

  private applyOffsetSample(sampleOffset: number, nowMonoMs: number): void {
    if (!Number.isFinite(sampleOffset)) return

    if (this.hasOffset) {
      const offsetJump = sampleOffset - this.offsetMs
      if (Math.abs(offsetJump) > NETCODE_V4_CLOCK_INSTABILITY_JUMP_MS) {
        this.clockUnstableCount++
        const clampedSample = this.offsetMs + Math.sign(offsetJump) * NETCODE_V4_CLOCK_INSTABILITY_CLAMP_MS
        this.offsetMs = this.offsetMs * (1 - NETCODE_V4_CLOCK_OFFSET_ALPHA) + clampedSample * NETCODE_V4_CLOCK_OFFSET_ALPHA
        if (this.clockUnstableCount >= NETCODE_V4_CLOCK_UNSTABLE_SAMPLE_LIMIT) {
          this.pendingResync = true
          this.holdNewestUntilMonoMs = nowMonoMs + NETCODE_V4_CLOCK_HOLD_NEWEST_MS
        }
        return
      }

      this.clockUnstableCount = 0
    }

    this.offsetMs = this.hasOffset
      ? this.offsetMs * (1 - NETCODE_V4_CLOCK_OFFSET_ALPHA) + sampleOffset * NETCODE_V4_CLOCK_OFFSET_ALPHA
      : sampleOffset
    this.hasOffset = true
  }
}
